import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatestWith, defer, distinctUntilChanged, firstValueFrom, from, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { DashboardPreferencesService } from '../dashboard-preferences/dashboard-preferences.service';

export interface DashboardFilters {
  worklist: string;
  page: number;
  intent: string | null;
  assignedTo: string | null;
  search: string | null;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  startDate: Date;
  endDate: Date;
}

@Injectable({ providedIn: 'root' })
export class DashboardFiltersService {
  private static readonly urlParamKeys: Array<keyof DashboardFilters> = [
    'page',
    'assignedTo',
    'worklist',
    'intent',
    'search',
    'sortBy',
    'sortOrder',
    'startDate',
    'endDate',
  ];
  private static readonly transientFields: Array<keyof DashboardFilters> = ['page', 'assignedTo'];
  private static readonly pageResetFields: Array<keyof DashboardFilters> = ['worklist', 'intent', 'search', 'startDate', 'endDate'];

  private readonly agentName$ = defer(() => from(this.authService.getCurrentUserProfile())).pipe(
    map(user => `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()),
    shareReplay(1),
  );

  public readonly filters$: Observable<DashboardFilters> = this.activatedRoute.queryParams.pipe(
    combineLatestWith(this.preferencesService.preferences$),
    map(([params, prefs]) => {
      const hasUrlState = DashboardFiltersService.urlParamKeys.some(key => params[key] != null);

      if (hasUrlState) {
        const now = new Date();
        const defaultStart = new Date(now);
        defaultStart.setDate(defaultStart.getDate() - DashboardPreferencesService.defaultStartDateOffsetDays);
        return {
          page: params['page'] ? parseInt(params['page'], 10) : 1,
          assignedTo: params['assignedTo'] ?? null,
          worklist: params['worklist'] ?? DashboardPreferencesService.defaultWorklist,
          intent: params['intent'] ?? null,
          search: params['search'] ?? null,
          sortBy: params['sortBy'],
          sortOrder: params['sortOrder'] as 'asc' | 'desc' | undefined,
          startDate: params['startDate'] ? new Date(params['startDate']) : defaultStart,
          endDate: params['endDate'] ? new Date(params['endDate']) : now,
        };
      }

      return {
        page: 1,
        assignedTo: null,
        worklist: prefs.worklist,
        intent: prefs.intent ?? null,
        search: prefs.search ?? null,
        sortBy: prefs.sortBy,
        sortOrder: prefs.sortOrder as 'asc' | 'desc' | undefined,
        startDate: prefs.startDate,
        endDate: prefs.endDate,
      };
    }),
    switchMap(filters =>
      filters.worklist === 'assignedToMe' && !filters.assignedTo
        ? this.agentName$.pipe(map(agentName => ({ ...filters, assignedTo: agentName })))
        : of(filters),
    ),
    distinctUntilChanged(
      (prev, curr) =>
        prev.page === curr.page &&
        prev.assignedTo === curr.assignedTo &&
        prev.worklist === curr.worklist &&
        prev.intent === curr.intent &&
        prev.search === curr.search &&
        prev.sortBy === curr.sortBy &&
        prev.sortOrder === curr.sortOrder &&
        prev.startDate.getTime() === curr.startDate.getTime() &&
        prev.endDate.getTime() === curr.endDate.getTime(),
    ),
  );
  constructor(
    private preferencesService: DashboardPreferencesService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
  ) {}

  async updateFilters(filters: Partial<DashboardFilters>): Promise<void> {
    const currentFilters = await firstValueFrom(this.filters$);
    const merged = { ...currentFilters, ...filters };
    if (merged.startDate > merged.endDate) {
      throw new Error('Start date must be less than or equal to end date');
    }
    const changedKeys = Object.keys(filters).filter(key =>
      this.valueChanged(filters[key as keyof DashboardFilters], currentFilters[key as keyof DashboardFilters]),
    );
    const currentParams = await firstValueFrom(this.activatedRoute.queryParams);
    const hasUrlState = DashboardFiltersService.urlParamKeys.some(key => currentParams[key] != null);
    if (changedKeys.length === 0 && hasUrlState) {
      return;
    }
    const changedPrefs = Object.fromEntries(
      changedKeys
        .filter(key => !DashboardFiltersService.transientFields.includes(key as keyof DashboardFilters))
        .map(key => [key, filters[key as keyof DashboardFilters]]),
    );
    if (Object.keys(changedPrefs).length > 0) {
      await firstValueFrom(this.preferencesService.updatePreferences(changedPrefs));
    }
    const resetsPage = changedKeys.some(key => DashboardFiltersService.pageResetFields.includes(key as keyof DashboardFilters));
    if (resetsPage) {
      merged.page = 1;
    }
    if (merged.worklist === 'assignedToMe' && !merged.assignedTo) {
      merged.assignedTo = await firstValueFrom(this.agentName$);
    }
    await this.updateUrl(merged);
  }

  private valueChanged(value: unknown, current: unknown): boolean {
    if (value instanceof Date && current instanceof Date) {
      return value.getTime() !== current.getTime();
    }
    return value !== current;
  }

  private async updateUrl(filters: DashboardFilters): Promise<void> {
    const queryParams: Record<string, string | null> = {
      page: filters.page !== 1 ? String(filters.page) : null,
      assignedTo: filters.assignedTo,
      worklist: filters.worklist ?? null,
      intent: filters.intent,
      search: filters.search,
      sortBy: filters.sortBy ?? null,
      sortOrder: filters.sortOrder ?? null,
      startDate: filters.startDate?.toISOString() ?? null,
      endDate: filters.endDate?.toISOString() ?? null,
    };

    const currentPath = this.router.url.split('?')[0];
    await this.router.navigate([currentPath], {
      queryParams,
    });
  }
}
